/**
 * Admin · Inventory (Phase 2)
 * Segregated stock table, filters, add-brand + add-component modals,
 * bulk-upload panels (Fresh & Older), Older-Stock ledger with Promote
 * action and (SuperAdmin-only) Add-Older-Stock manual entry.
 *
 * Every action hits an existing backend endpoint verbatim:
 *   GET    /api/brands
 *   POST   /api/brands
 *   GET    /api/components
 *   POST   /api/components
 *   POST   /api/components/bulk-upload
 *   POST   /api/components/bulk-upload-older-stock
 *   GET    /api/components/bulk-upload-template            (xlsx)
 *   GET    /api/components/bulk-upload-older-stock-template (xlsx)
 *   POST   /api/components/{id}/promote-older-to-reissuable
 *   POST   /api/components/{id}/add-older-stock             (SuperAdmin)
 *
 * No schema, backend or data changes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Package, Plus, Recycle, Upload, Download, ArrowUpRight, Search, X, FileSpreadsheet, FileDown, Printer,
  Trash2, AlertTriangle,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import {
  Card, EmptyState, StatusPill, SearchInput, Modal,
} from '../ui';
import OTPModal from '../../superadmin/OTPModal';

const stockVariant = (c) => {
  const avail = c.stock_quantity ?? 0;
  const thr   = c.low_stock_threshold ?? 5;
  if (avail === 0) return { variant: 'bad',  label: 'Out of stock' };
  if (avail <= thr) return { variant: 'warn', label: 'Low stock'   };
  return { variant: 'ok', label: 'In stock' };
};

const LOGO_URL_PRINT = 'https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png';

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const DEFAULT_COMPONENT_FORM = {
  brand_id: '',
  name: '',
  description: '',
  size: '',
  stock_quantity: 0,
  low_stock_threshold: 5,
  price: 0,
  is_returnable: true,
};

export default function InventoryPage({ ctx }) {
  const { user, showToast } = ctx;

  const [brands, setBrands] = useState([]);
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [brandFilter, setBrandFilter] = useState('');
  const [nameFilter, setNameFilter]   = useState('');
  const [sizeFilter, setSizeFilter]   = useState('');
  const [olderBrandFilter, setOlderBrandFilter] = useState('');
  const [olderNameFilter, setOlderNameFilter]   = useState('');

  // Modals
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [brandForm, setBrandForm] = useState({ name: '', description: '' });
  const [creatingBrand, setCreatingBrand] = useState(false);

  const [componentModalOpen, setComponentModalOpen] = useState(false);
  const [componentForm, setComponentForm] = useState(DEFAULT_COMPONENT_FORM);
  const [creatingComponent, setCreatingComponent] = useState(false);

  const [bulkUpload, setBulkUpload] = useState({ open: false, mode: 'fresh', file: null, uploading: false, result: null });
  const bulkInputRef = useRef(null);

  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoteQty, setPromoteQty] = useState(1);
  const [promoting, setPromoting] = useState(false);

  const [addOlderOpen, setAddOlderOpen] = useState(false);
  const [addOlderSearch, setAddOlderSearch] = useState({ brand: '', component: '', size: '' });
  const [addOlderPick, setAddOlderPick] = useState(null);
  const [addOlderQty, setAddOlderQty] = useState(1);
  const [addOlderNote, setAddOlderNote] = useState('');
  const [addingOlder, setAddingOlder] = useState(false);

  // ── Danger Zone (SuperAdmin only) ──
  // Two-step UX: 1) confirmation modal, 2) OTP modal, 3) action with OTP headers.
  const [dangerAction, setDangerAction] = useState(null);     // 'bulk_delete' | 'master_wipe' | null — opens confirmation modal
  const [otpAction, setOtpAction]       = useState(null);     // same keys — opens OTP modal after confirmation
  const [dangerBusy, setDangerBusy]     = useState(false);
  const [dangerResult, setDangerResult] = useState(null);

  const canWrite = user?.role === 'admin' || user?.role === 'superadmin';
  const isSuperAdmin = user?.role === 'superadmin';

  const loadBrands = async () => {
    try {
      const { data } = await apiClient.get(endpoints.brands.list);
      setBrands(Array.isArray(data) ? data : []);
    } catch (e) { console.error('brands', e); }
  };
  const loadComponents = async () => {
    try {
      const { data } = await apiClient.get(endpoints.components.list);
      setComponents(Array.isArray(data) ? data : []);
    } catch (e) { console.error('components', e); }
  };
  const refresh = async () => {
    setLoading(true);
    await Promise.all([loadBrands(), loadComponents()]);
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    return components
      .filter((c) => (brandFilter ? c.brand_id === brandFilter : true))
      .filter((c) => (nameFilter ? c.name.toLowerCase().includes(nameFilter.toLowerCase()) : true))
      .filter((c) => (sizeFilter ? (c.size || '').toLowerCase().includes(sizeFilter.toLowerCase()) : true));
  }, [components, brandFilter, nameFilter, sizeFilter]);

  const olderRows = useMemo(() => {
    return components
      .filter((c) => (c.returned_old_stock || 0) > 0)
      .filter((c) => (olderBrandFilter ? c.brand_id === olderBrandFilter : true))
      .filter((c) => (olderNameFilter ? c.name.toLowerCase().includes(olderNameFilter.toLowerCase()) : true));
  }, [components, olderBrandFilter, olderNameFilter]);

  const brandName = (id) =>
    (brands.find((b) => b.id === id) || {}).name || 'Unknown';

  /* ── Brand create ─────────────────────────────────────── */
  const createBrand = async (e) => {
    e.preventDefault();
    if (!brandForm.name.trim() || creatingBrand) return;
    setCreatingBrand(true);
    try {
      await apiClient.post(endpoints.brands.create, brandForm);
      showToast(`Brand added · ${brandForm.name}`, 'ok');
      setBrandForm({ name: '', description: '' });
      setBrandModalOpen(false);
      await loadBrands();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Failed to create brand.', 'bad');
    } finally { setCreatingBrand(false); }
  };

  /* ── Component create ─────────────────────────────────── */
  const createComponent = async (e) => {
    e.preventDefault();
    if (creatingComponent) return;
    if (!componentForm.brand_id || !componentForm.name.trim() || !componentForm.size.trim()) {
      showToast('Brand, component name and size are required.', 'bad');
      return;
    }
    setCreatingComponent(true);
    try {
      await apiClient.post(endpoints.components.create, componentForm);
      showToast(`Component added · ${componentForm.name}`, 'ok');
      setComponentForm(DEFAULT_COMPONENT_FORM);
      setComponentModalOpen(false);
      await loadComponents();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Failed to create component.', 'bad');
    } finally { setCreatingComponent(false); }
  };

  /* ── Bulk upload ──────────────────────────────────────── */
  const downloadTemplate = async () => {
    try {
      const url = bulkUpload.mode === 'older'
        ? endpoints.components.bulkUploadOlderTemplate
        : endpoints.components.bulkUploadTemplate;
      const response = await apiClient.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `SAID_DIIS_${bulkUpload.mode === 'older' ? 'Older_Stock' : 'Fresh_Stock'}_Template.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Template download failed.', 'bad');
    }
  };

  const submitBulkUpload = async (e) => {
    e.preventDefault();
    if (!bulkUpload.file || bulkUpload.uploading) return;
    setBulkUpload((s) => ({ ...s, uploading: true, result: null }));
    const fd = new FormData();
    fd.append('file', bulkUpload.file);
    const url = bulkUpload.mode === 'older'
      ? endpoints.components.bulkUploadOlderStock
      : endpoints.components.bulkUpload;
    try {
      const { data } = await apiClient.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkUpload((s) => ({ ...s, uploading: false, result: data, file: null }));
      if (bulkInputRef.current) bulkInputRef.current.value = '';
      showToast(`${data.success_count || 0} row(s) applied.`, 'ok');
      await loadComponents();
    } catch (err) {
      setBulkUpload((s) => ({
        ...s,
        uploading: false,
        result: {
          success_count: 0,
          error_count: 1,
          errors: [err?.response?.data?.detail || 'Upload failed.'],
        },
      }));
    }
  };

  /* ── Promote older → reissuable ───────────────────────── */
  const submitPromote = async () => {
    if (!promoteTarget || promoting || promoteQty < 1) return;
    setPromoting(true);
    try {
      await apiClient.post(
        endpoints.components.promoteOlderToReissuable(promoteTarget.id),
        { quantity: promoteQty },
      );
      showToast(`Promoted ${promoteQty} · ${promoteTarget.name}`, 'ok');
      setPromoteTarget(null);
      setPromoteQty(1);
      await loadComponents();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Promote failed.', 'bad');
    } finally { setPromoting(false); }
  };

  /* ── Add Older Stock (SuperAdmin only) ─────────────────── */
  const addOlderMatches = useMemo(() => {
    return components
      .filter((c) => (addOlderSearch.brand ? c.brand_id === addOlderSearch.brand : true))
      .filter((c) => (addOlderSearch.component ? c.name.toLowerCase().includes(addOlderSearch.component.toLowerCase()) : true))
      .filter((c) => (addOlderSearch.size ? (c.size || '').toLowerCase().includes(addOlderSearch.size.toLowerCase()) : true))
      .slice(0, 30);
  }, [components, addOlderSearch]);

  const submitAddOlder = async () => {
    if (!addOlderPick || addOlderQty < 1 || addingOlder) return;
    setAddingOlder(true);
    try {
      await apiClient.post(
        endpoints.components.addOlderStock(addOlderPick.id),
        { quantity: addOlderQty, source_note: addOlderNote.trim() },
      );
      showToast(`Added ${addOlderQty} to older stock · ${addOlderPick.name}`, 'ok');
      setAddOlderOpen(false);
      setAddOlderPick(null);
      setAddOlderQty(1);
      setAddOlderNote('');
      setAddOlderSearch({ brand: '', component: '', size: '' });
      await loadComponents();
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Failed to add older stock.', 'bad');
    } finally { setAddingOlder(false); }
  };

  const clearFilters = () => { setBrandFilter(''); setNameFilter(''); setSizeFilter(''); };

  /* ── Danger Zone handlers (SuperAdmin only, OTP-gated) ───────
   * Both flows are shaped identically:
   *   1. User clicks the button → `dangerAction` opens the confirmation card.
   *   2. "Continue → OTP" button closes confirm, opens `otpAction` modal.
   *   3. OTPModal sends { challengeId, code } here; we fire the real request
   *      with X-OTP-* headers. Server-side `require_otp()` consumes the
   *      challenge atomically; failures bubble back into OTPModal error UI.
   */
  const runBulkDelete = async ({ challengeId, code }) => {
    setDangerBusy(true);
    try {
      const { data } = await apiClient.delete(endpoints.components.superadminBulkDelete, {
        headers: { 'X-OTP-Challenge-Id': challengeId, 'X-OTP-Code': code },
      });
      showToast(`Bulk delete complete — ${data?.deleted_count ?? 0} removed`, 'ok');
      setOtpAction(null);
      setDangerResult({ kind: 'bulk_delete', count: data?.deleted_count ?? 0 });
      await refresh();
    } finally { setDangerBusy(false); }
  };

  const runMasterWipe = async ({ challengeId, code }) => {
    setDangerBusy(true);
    try {
      const { data } = await apiClient.put(endpoints.components.masterReset, null, {
        headers: { 'X-OTP-Challenge-Id': challengeId, 'X-OTP-Code': code },
      });
      showToast('Master wipe complete — inventory reset to zero', 'ok');
      setOtpAction(null);
      setDangerResult({ kind: 'master_wipe', snapshot: data });
      await refresh();
    } finally { setDangerBusy(false); }
  };

  /* ── CSV export of the currently-visible (post-filter) rows ──
   * Pure client-side — reads the same `filtered` array the table
   * renders. No API call, no re-computation, no mutation to inventory
   * values. The file mirrors on-screen columns exactly. */
  const escapeCsvCell = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const downloadCsv = () => {
    if (filtered.length === 0) {
      showToast('No rows in the current view to export.', 'bad');
      return;
    }
    const headers = [
      'Component', 'Brand', 'Size',
      'Total Stock', 'Issued', 'Available', 'Re-issuable', 'Returned Old',
      'Threshold', 'Price (INR)', 'Returnable', 'Status',
    ];
    const rows = filtered.map((c) => {
      const s = stockVariant(c);
      return [
        c.name, c.brand_name || brandName(c.brand_id), c.size || '',
        c.total_stock ?? c.stock_quantity ?? 0,
        c.issued_qty || 0,
        c.stock_quantity ?? 0,
        c.reissuable_stock || 0,
        c.returned_old_stock || 0,
        c.low_stock_threshold,
        c.price ?? 0,
        c.is_returnable ? 'Yes' : 'No',
        s.label,
      ];
    });
    const csv = '\ufeff' + [headers, ...rows]
      .map((r) => r.map(escapeCsvCell).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    link.href = url;
    link.download = `SAID_DIIS_Inventory_${stamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    showToast(`Exported ${filtered.length} row${filtered.length === 1 ? '' : 's'}.`, 'ok');
  };

  /* ── One-page printable snapshot (DIIS letterhead) ──
   * Opens a new window with a self-contained HTML document formatted
   * for A4-landscape printing. User can save-as-PDF from the browser
   * print dialog. Zero dependencies, zero backend calls, zero mutation
   * to inventory values — same `filtered` array powers both this and
   * the CSV export. */
  const printSnapshot = () => {
    if (filtered.length === 0) {
      showToast('No rows in the current view to print.', 'bad');
      return;
    }
    const now = new Date();
    const stamp = now.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
    const filters = [
      brandFilter && `Brand · ${brandName(brandFilter)}`,
      nameFilter  && `Component · “${nameFilter}”`,
      sizeFilter  && `Size · “${sizeFilter}”`,
    ].filter(Boolean).join('  ·  ') || 'All components';

    const rowsHtml = filtered.map((c) => {
      const s = stockVariant(c);
      return `<tr>
        <td><strong>${escapeHtml(c.name)}</strong>${c.size ? `<div class="sub">${escapeHtml(c.size)}${c.is_returnable ? ' · Returnable' : ''}</div>` : (c.is_returnable ? '<div class="sub">Returnable</div>' : '')}</td>
        <td>${escapeHtml(c.brand_name || brandName(c.brand_id))}</td>
        <td class="num">${c.total_stock ?? c.stock_quantity ?? 0}</td>
        <td class="num warn">${c.issued_qty || 0}</td>
        <td class="num strong">${c.stock_quantity ?? 0}</td>
        <td class="num accent">${c.reissuable_stock || 0}</td>
        <td class="num ok">${c.returned_old_stock || 0}</td>
        <td class="num">${c.low_stock_threshold}</td>
        <td class="num">₹${c.price ?? 0}</td>
        <td class="status ${s.variant}">${escapeHtml(s.label)}</td>
      </tr>`;
    }).join('');

    const html = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>SAID · DIIS · Inventory Snapshot · ${escapeHtml(stamp)}</title>
  <style>
    @page { size: A4 landscape; margin: 12mm 12mm 14mm; }
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, "Helvetica Neue", Arial, sans-serif;
      color: #10202D; margin: 0; padding: 0;
      -webkit-print-color-adjust: exact; print-color-adjust: exact;
    }
    header {
      display: flex; align-items: center; justify-content: space-between;
      padding-bottom: 12px; margin-bottom: 14px;
      border-bottom: 2px solid #10202D;
    }
    .brand { display: flex; align-items: center; gap: 14px; }
    .brand img { height: 44px; width: auto; }
    .brand .name { font-family: Georgia, "Times New Roman", serif; font-weight: 500; font-size: 20px; letter-spacing: -0.01em; }
    .brand .sub { font-size: 9.5px; letter-spacing: 0.22em; text-transform: uppercase; color: #6b7c8a; margin-top: 3px; }
    .meta { text-align: right; font-size: 10.5px; color: #6b7c8a; line-height: 1.6; }
    .meta strong { color: #10202D; font-weight: 600; letter-spacing: 0.02em; }
    h1 { font-family: Georgia, "Times New Roman", serif; font-weight: 500; font-size: 17px; margin: 0 0 3px; letter-spacing: -0.01em; }
    .lede { font-size: 11px; color: #6b7c8a; margin: 0 0 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    thead th {
      text-align: left; font-size: 9px; letter-spacing: 0.12em;
      text-transform: uppercase; color: #6b7c8a; font-weight: 600;
      padding: 8px 8px; border-bottom: 1px solid #dbe0e6;
      white-space: nowrap;
    }
    thead th.num { text-align: right; }
    tbody td { padding: 7px 8px; border-bottom: 1px solid #eef1f4; vertical-align: top; }
    tbody td .sub { font-size: 9.5px; color: #6b7c8a; margin-top: 2px; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .num.warn { color: #A96417; }
    .num.strong { font-weight: 600; }
    .num.accent { color: #2E7E82; font-weight: 600; }
    .num.ok { color: #14432A; font-weight: 600; }
    .status { text-transform: uppercase; letter-spacing: 0.05em; font-size: 9.5px; font-weight: 600; }
    .status.ok { color: #14432A; }
    .status.warn { color: #A96417; }
    .status.bad { color: #B04A3C; }
    footer {
      position: fixed; bottom: 6mm; left: 12mm; right: 12mm;
      font-size: 9px; color: #6b7c8a; text-align: center;
      border-top: 1px solid #eef1f4; padding-top: 4px;
    }
    .sig-row {
      display: flex; justify-content: space-between; gap: 60px;
      margin-top: 42px; page-break-inside: avoid;
    }
    .sig { flex: 1; }
    .sig .line { border-top: 1px solid #10202D; margin-top: 34px; }
    .sig .label { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; color: #6b7c8a; margin-top: 6px; }
    @media print {
      header { break-inside: avoid; }
      tbody tr { break-inside: avoid; }
      .no-print { display: none; }
    }
    .no-print {
      position: fixed; top: 8px; right: 8px;
      background: #10202D; color: #F7F5F0; border: 0;
      padding: 8px 14px; border-radius: 8px; cursor: pointer;
      font-family: inherit; font-size: 12px;
    }
  </style>
</head>
<body>
  <button class="no-print" onclick="window.print()">Print / Save PDF</button>
  <header>
    <div class="brand">
      <img src="${LOGO_URL_PRINT}" alt="SAID">
      <div>
        <div class="name">Saveetha Dental College &amp; Hospitals</div>
        <div class="sub">SAID · DIIS · Dental Implant Inventory System</div>
      </div>
    </div>
    <div class="meta">
      <strong>Inventory Snapshot</strong><br>
      ${escapeHtml(stamp)}<br>
      ${escapeHtml(filters)}<br>
      ${filtered.length} row${filtered.length === 1 ? '' : 's'}
    </div>
  </header>

  <h1>Segregated Stock Ledger</h1>
  <p class="lede">Fresh, issued, re-issuable and returned-old pools are tracked independently — this table never auto-mixes them.</p>

  <table>
    <thead>
      <tr>
        <th>Component</th>
        <th>Brand</th>
        <th class="num">Total</th>
        <th class="num">Issued</th>
        <th class="num">Available</th>
        <th class="num">Re-issuable</th>
        <th class="num">Returned Old</th>
        <th class="num">Threshold</th>
        <th class="num">Price</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>${rowsHtml}</tbody>
  </table>

  <div class="sig-row">
    <div class="sig"><div class="line"></div><div class="label">Prepared by · Record Clerk</div></div>
    <div class="sig"><div class="line"></div><div class="label">Verified by · Faculty</div></div>
    <div class="sig"><div class="line"></div><div class="label">Head of Department</div></div>
  </div>

  <footer>Generated ${escapeHtml(stamp)}  ·  ${escapeHtml(filters)}  ·  ${filtered.length} row${filtered.length === 1 ? '' : 's'}  ·  SAID · DIIS</footer>

  <script>window.onload = function () { setTimeout(function () { window.print(); }, 400); };</script>
</body>
</html>`;

    const w = window.open('', '_blank');
    if (!w) {
      showToast('Please allow pop-ups to print the snapshot.', 'bad');
      return;
    }
    w.document.open();
    w.document.write(html);
    w.document.close();
    showToast(`Snapshot ready · ${filtered.length} row${filtered.length === 1 ? '' : 's'}.`, 'ok');
  };

  return (
    <div data-testid="ap-inventory-page">
      <div className="ap-eyebrow">Catalogue</div>
      <h1 className="ap-page-title">Inventory</h1>
      <p className="ap-page-lede">
        The segregated stock ledger. Fresh, issued, re-issuable and returned-old pools are
        tracked independently — this table never auto-mixes them.
      </p>

      {/* Toolbar */}
      <Card className="ap-mt-5" testId="ap-inventory-toolbar">
        <div className="ap-filter-row">
          <div>
            <label className="ap-field-label" htmlFor="ap-inv-brand">Brand</label>
            <select
              id="ap-inv-brand"
              className="ap-select"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              data-testid="ap-inv-brand-filter"
            >
              <option value="">All brands</option>
              {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </div>
          <div>
            <label className="ap-field-label" htmlFor="ap-inv-name">Component</label>
            <SearchInput
              value={nameFilter}
              onChange={setNameFilter}
              placeholder="Search by name…"
              testId="ap-inv-name-filter"
            />
          </div>
          <div>
            <label className="ap-field-label" htmlFor="ap-inv-size">Size</label>
            <input
              id="ap-inv-size"
              type="text"
              className="ap-input"
              value={sizeFilter}
              onChange={(e) => setSizeFilter(e.target.value)}
              placeholder="e.g. 4.3 × 11.5"
              data-testid="ap-inv-size-filter"
            />
          </div>
          <div>
            <label className="ap-field-label">&nbsp;</label>
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={clearFilters}
              data-testid="ap-inv-clear-filters"
            >Clear</button>
          </div>
          <div>
            <label className="ap-field-label">&nbsp;</label>
            <button
              type="button"
              className="ap-btn"
              onClick={downloadCsv}
              disabled={filtered.length === 0}
              title="Export the components currently visible in the table as a CSV. Applied filters are respected."
              data-testid="ap-inv-download-csv"
            >
              <FileDown size={15} /> Download CSV
            </button>
          </div>
          <div>
            <label className="ap-field-label">&nbsp;</label>
            <button
              type="button"
              className="ap-btn"
              onClick={printSnapshot}
              disabled={filtered.length === 0}
              title="Open a one-page printable snapshot (with DIIS letterhead) of the current filtered view."
              data-testid="ap-inv-print-snapshot"
            >
              <Printer size={15} /> Print Snapshot
            </button>
          </div>
        </div>

        {canWrite && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }} data-testid="ap-inv-actions">
            <button
              type="button"
              className="ap-btn ap-btn-primary"
              onClick={() => setComponentModalOpen(true)}
              data-testid="ap-inv-add-component"
            >
              <Plus size={15} /> Add Component
            </button>
            <button
              type="button"
              className="ap-btn"
              onClick={() => setBrandModalOpen(true)}
              data-testid="ap-inv-add-brand"
            >
              <Plus size={15} /> Add Brand
            </button>
            <button
              type="button"
              className="ap-btn"
              onClick={() => setBulkUpload({ open: true, mode: 'fresh', file: null, uploading: false, result: null })}
              data-testid="ap-inv-bulk-fresh"
            >
              <Upload size={15} /> Bulk Upload · Fresh
            </button>
            <button
              type="button"
              className="ap-btn"
              onClick={() => setBulkUpload({ open: true, mode: 'older', file: null, uploading: false, result: null })}
              data-testid="ap-inv-bulk-older"
            >
              <Recycle size={15} /> Bulk Upload · Older
            </button>
          </div>
        )}
      </Card>

      {/* ── SuperAdmin Danger Zone (OTP-gated) ────────────────── */}
      {isSuperAdmin && (
        <div className="ap-mt-5" data-testid="ap-inv-danger-zone">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: 14,
            }}
          >
            <div
              style={{
                border: '1px solid rgba(176, 74, 60, 0.25)',
                background: 'rgba(176, 74, 60, 0.04)',
                borderRadius: 10,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#B04A3C', marginBottom: 6 }}>
                <Trash2 size={16} />
                <strong style={{ fontSize: 14 }}>Bulk Delete Components</strong>
              </div>
              <p style={{ fontSize: 13, color: '#6B2E23', margin: '0 0 12px', lineHeight: 1.55 }}>
                Removes every component in the catalogue. Brands, orders, returns and waitlist are preserved.
                {` `}Current catalogue: <strong>{components.length}</strong>.
              </p>
              <button
                type="button"
                className="ap-btn ap-btn-danger"
                onClick={() => { setDangerResult(null); setDangerAction('bulk_delete'); }}
                disabled={components.length === 0}
                data-testid="ap-inv-bulk-delete"
              >
                <Trash2 size={14} /> Bulk Delete
              </button>
            </div>

            <div
              style={{
                border: '1px solid rgba(176, 74, 60, 0.4)',
                background: 'rgba(176, 74, 60, 0.08)',
                borderRadius: 10,
                padding: 16,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#8B2E1F', marginBottom: 6 }}>
                <AlertTriangle size={16} />
                <strong style={{ fontSize: 14 }}>Master Wipe · Full Reset</strong>
              </div>
              <p style={{ fontSize: 13, color: '#6B2E23', margin: '0 0 12px', lineHeight: 1.55 }}>
                Wipes components, brands, orders, returns, waitlist, older-stock returns, exchanges, and activity logs.
                {` `}Users and defaulter-overrides are preserved.
              </p>
              <button
                type="button"
                className="ap-btn ap-btn-danger"
                onClick={() => { setDangerResult(null); setDangerAction('master_wipe'); }}
                data-testid="ap-inv-master-wipe"
              >
                <AlertTriangle size={14} /> Master Wipe
              </button>
            </div>
          </div>

          {dangerResult && (
            <div
              data-testid="ap-inv-danger-result"
              style={{
                marginTop: 14,
                padding: '10px 12px',
                borderRadius: 8,
                background: '#f0fdf4',
                border: '1px solid #86efac',
                color: '#166534',
                fontSize: 13,
              }}
            >
              {dangerResult.kind === 'bulk_delete'
                ? `Bulk delete complete — removed ${dangerResult.count} component${dangerResult.count === 1 ? '' : 's'}.`
                : `Master wipe complete — inventory reset to zero.`}
            </div>
          )}
        </div>
      )}

      {/* Segregated stock table */}
      <Card
        className="ap-mt-5"
        title="Components"
        subtitle={`${filtered.length} of ${components.length} visible`}
        testId="ap-inv-main-card"
      >
        {filtered.length === 0 ? (
          <EmptyState
            icon={Package}
            title={components.length === 0 ? 'No components in the catalogue yet' : 'No components match your filters'}
            message={components.length === 0
              ? 'Add a brand and a component to get started, or use bulk upload for a spreadsheet.'
              : 'Try loosening the brand / component / size filters.'}
            testId="ap-inv-empty"
          />
        ) : (
          <div style={{ overflowX: 'auto', margin: '0 -6px' }}>
            <table className="ap-kit-table" style={{ margin: 0, minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Brand</th>
                  <th style={{ textAlign: 'right' }} title="Fresh untouched stock. Never decreases on issue; only decreases when a returned unit is moved into Re-issuable (post-sterilisation gate).">Total</th>
                  <th style={{ textAlign: 'right' }} title="Units currently in students' hands.">Issued</th>
                  <th style={{ textAlign: 'right' }} title="Total − Issued.">Available</th>
                  <th style={{ textAlign: 'right' }} title="Verified older units promoted for re-issue — drained first.">Re-issuable</th>
                  <th style={{ textAlign: 'right' }} title="Older / previously-used units returned & verified — separate pool.">Returned Old</th>
                  <th style={{ textAlign: 'right' }}>Threshold</th>
                  <th style={{ textAlign: 'right' }}>Price</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const s = stockVariant(c);
                  return (
                    <tr key={c.id} data-testid={`ap-inv-row-${c.id}`}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{c.name}</div>
                        <div className="ap-subtle">{c.size ? c.size : '—'}{c.is_returnable ? ' · Returnable' : ''}</div>
                      </td>
                      <td>{c.brand_name || brandName(c.brand_id)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 500 }} data-testid={`ap-inv-total-${c.id}`}>
                        {c.total_stock ?? c.stock_quantity ?? 0}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--diis-warning)' }} data-testid={`ap-inv-issued-${c.id}`}>
                        {c.issued_qty || 0}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }} data-testid={`ap-inv-available-${c.id}`}>
                        {c.stock_quantity ?? 0}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--diis-accent-ink)', fontWeight: 600 }} data-testid={`ap-inv-reissuable-${c.id}`}>
                        {c.reissuable_stock || 0}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--diis-success)', fontWeight: 600 }} data-testid={`ap-inv-old-${c.id}`}>
                        {c.returned_old_stock || 0}
                      </td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.low_stock_threshold}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>₹{c.price ?? 0}</td>
                      <td><StatusPill variant={s.variant}>{s.label}</StatusPill></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Older-stock ledger */}
      <Card
        className="ap-mt-5"
        title="Returned & Older Stock"
        subtitle="Previously-used components returned and verified. This pool is separate from fresh stock and never auto-issued."
        actions={isSuperAdmin ? (
          <button
            type="button"
            className="ap-btn"
            onClick={() => setAddOlderOpen(true)}
            data-testid="ap-inv-open-add-older"
          >
            <Plus size={15} /> Add Older Stock
          </button>
        ) : null}
        testId="ap-inv-older-card"
      >
        <div className="ap-filter-row" style={{ gridTemplateColumns: '1fr 1fr auto', marginBottom: 12 }}>
          <div>
            <label className="ap-field-label">Brand</label>
            <select
              className="ap-select"
              value={olderBrandFilter}
              onChange={(e) => setOlderBrandFilter(e.target.value)}
              data-testid="ap-older-filter-brand"
            >
              <option value="">All brands</option>
              {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </div>
          <div>
            <label className="ap-field-label">Component</label>
            <SearchInput
              value={olderNameFilter}
              onChange={setOlderNameFilter}
              placeholder="Filter by name…"
              testId="ap-older-filter-component"
            />
          </div>
          <div>
            <label className="ap-field-label">&nbsp;</label>
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={() => { setOlderBrandFilter(''); setOlderNameFilter(''); }}
              data-testid="ap-older-filter-clear"
            >Clear</button>
          </div>
        </div>

        {olderRows.length === 0 ? (
          <EmptyState
            icon={Recycle}
            title="No older / returned stock yet"
            message="Once returns are verified or older stock is uploaded, this ledger will fill in."
            testId="ap-older-empty"
          />
        ) : (
          <div style={{ overflowX: 'auto', margin: '0 -6px' }}>
            <table className="ap-kit-table" style={{ margin: 0 }} data-testid="ap-older-table">
              <thead>
                <tr>
                  <th>Brand</th>
                  <th>Component</th>
                  <th>Size</th>
                  <th style={{ textAlign: 'right' }}>Returned Old</th>
                  <th style={{ textAlign: 'right' }}>Re-issuable</th>
                  <th style={{ textAlign: 'right' }}>Fresh Available</th>
                  {canWrite && <th style={{ textAlign: 'right' }}>Action</th>}
                </tr>
              </thead>
              <tbody>
                {olderRows.map((c) => (
                  <tr key={c.id} data-testid={`ap-older-row-${c.id}`}>
                    <td>{c.brand_name || brandName(c.brand_id)}</td>
                    <td style={{ fontWeight: 500 }}>{c.name}</td>
                    <td className="ap-subtle">{c.size || '—'}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--diis-success)', fontWeight: 600 }}>{c.returned_old_stock || 0}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--diis-accent-ink)', fontWeight: 600 }}>{c.reissuable_stock || 0}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.stock_quantity || 0}</td>
                    {canWrite && (
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="ap-btn ap-btn-sm"
                          onClick={() => { setPromoteTarget(c); setPromoteQty(1); }}
                          data-testid={`ap-promote-older-btn-${c.id}`}
                          title="Move verified older units into the re-issuable pool"
                        >
                          <ArrowUpRight size={13} /> Promote
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Add Brand modal ────────────────────────────────── */}
      <Modal
        open={brandModalOpen}
        onClose={() => !creatingBrand && setBrandModalOpen(false)}
        title="Add Brand"
        subtitle="Groups components by manufacturer for filtering and reporting."
        testId="ap-brand-modal"
        footer={(
          <>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setBrandModalOpen(false)} disabled={creatingBrand} data-testid="ap-brand-cancel">Cancel</button>
            <button type="button" className="ap-btn ap-btn-accent" onClick={createBrand} disabled={creatingBrand || !brandForm.name.trim()} data-testid="ap-brand-submit">{creatingBrand ? 'Adding…' : 'Add Brand'}</button>
          </>
        )}
      >
        <form onSubmit={createBrand}>
          <div>
            <label className="ap-field-label" htmlFor="ap-brand-name">Brand name *</label>
            <input
              id="ap-brand-name"
              type="text"
              className="ap-input"
              value={brandForm.name}
              onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
              required
              autoFocus
              data-testid="ap-brand-name-input"
            />
          </div>
          <div className="ap-mt-4">
            <label className="ap-field-label" htmlFor="ap-brand-desc">Description (optional)</label>
            <textarea
              id="ap-brand-desc"
              className="ap-textarea"
              value={brandForm.description}
              onChange={(e) => setBrandForm({ ...brandForm, description: e.target.value })}
              data-testid="ap-brand-desc-input"
            />
          </div>
        </form>
      </Modal>

      {/* ── Add Component modal ────────────────────────────── */}
      <Modal
        open={componentModalOpen}
        onClose={() => !creatingComponent && setComponentModalOpen(false)}
        title="Add Component"
        subtitle="A new SKU in the catalogue. Stock is tracked independently per pool."
        wide
        testId="ap-component-modal"
        footer={(
          <>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setComponentModalOpen(false)} disabled={creatingComponent} data-testid="ap-component-cancel">Cancel</button>
            <button type="button" className="ap-btn ap-btn-accent" onClick={createComponent} disabled={creatingComponent || !componentForm.brand_id || !componentForm.name.trim() || !componentForm.size.trim()} data-testid="ap-component-submit">{creatingComponent ? 'Adding…' : 'Add Component'}</button>
          </>
        )}
      >
        <form onSubmit={createComponent}>
          <div className="ap-grid-2">
            <div>
              <label className="ap-field-label" htmlFor="ap-comp-brand">Brand *</label>
              <select
                id="ap-comp-brand"
                className="ap-select"
                value={componentForm.brand_id}
                onChange={(e) => setComponentForm({ ...componentForm, brand_id: e.target.value })}
                required
                data-testid="ap-component-brand"
              >
                <option value="">Select brand</option>
                {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </select>
            </div>
            <div>
              <label className="ap-field-label" htmlFor="ap-comp-size">Size *</label>
              <input
                id="ap-comp-size"
                type="text"
                className="ap-input"
                value={componentForm.size}
                onChange={(e) => setComponentForm({ ...componentForm, size: e.target.value })}
                placeholder="e.g. 4.3 × 11.5 mm"
                required
                data-testid="ap-component-size"
              />
            </div>
          </div>
          <div className="ap-mt-4">
            <label className="ap-field-label" htmlFor="ap-comp-name">Component name *</label>
            <input
              id="ap-comp-name"
              type="text"
              className="ap-input"
              value={componentForm.name}
              onChange={(e) => setComponentForm({ ...componentForm, name: e.target.value })}
              required
              data-testid="ap-component-name"
            />
          </div>
          <div className="ap-mt-4">
            <label className="ap-field-label" htmlFor="ap-comp-desc">Description</label>
            <textarea
              id="ap-comp-desc"
              className="ap-textarea"
              value={componentForm.description}
              onChange={(e) => setComponentForm({ ...componentForm, description: e.target.value })}
              rows={2}
              data-testid="ap-component-desc"
            />
          </div>
          <div className="ap-grid-2 ap-mt-4">
            <div>
              <label className="ap-field-label" htmlFor="ap-comp-stock">Initial fresh stock</label>
              <input
                id="ap-comp-stock"
                type="number"
                min="0"
                className="ap-input"
                value={componentForm.stock_quantity}
                onChange={(e) => setComponentForm({ ...componentForm, stock_quantity: parseInt(e.target.value || '0', 10) })}
                data-testid="ap-component-stock"
              />
            </div>
            <div>
              <label className="ap-field-label" htmlFor="ap-comp-thr">Low-stock threshold</label>
              <input
                id="ap-comp-thr"
                type="number"
                min="1"
                className="ap-input"
                value={componentForm.low_stock_threshold}
                onChange={(e) => setComponentForm({ ...componentForm, low_stock_threshold: parseInt(e.target.value || '1', 10) })}
                data-testid="ap-component-threshold"
              />
            </div>
          </div>
          <div className="ap-grid-2 ap-mt-4">
            <div>
              <label className="ap-field-label" htmlFor="ap-comp-price">Price (₹)</label>
              <input
                id="ap-comp-price"
                type="number"
                min="0"
                step="0.01"
                className="ap-input"
                value={componentForm.price}
                onChange={(e) => setComponentForm({ ...componentForm, price: parseFloat(e.target.value || '0') })}
                data-testid="ap-component-price"
              />
            </div>
            <div>
              <label className="ap-field-label">Return policy</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--diis-hairline-strong)', borderRadius: 'var(--diis-radius)', cursor: 'pointer', height: 42 }}>
                <input
                  type="checkbox"
                  checked={componentForm.is_returnable}
                  onChange={(e) => setComponentForm({ ...componentForm, is_returnable: e.target.checked })}
                  data-testid="ap-component-returnable"
                />
                <span style={{ fontSize: 13 }}>Returnable (uncheck for consumables)</span>
              </label>
            </div>
          </div>
        </form>
      </Modal>

      {/* ── Bulk upload modal ──────────────────────────────── */}
      <Modal
        open={bulkUpload.open}
        onClose={() => !bulkUpload.uploading && setBulkUpload({ ...bulkUpload, open: false, result: null, file: null })}
        title={bulkUpload.mode === 'older' ? 'Bulk Upload · Older Stock' : 'Bulk Upload · Fresh Stock'}
        subtitle={bulkUpload.mode === 'older'
          ? 'Append verified older units to EXISTING components — fresh stock is never touched.'
          : 'Create NEW components from an Excel sheet — download the template first for the exact schema.'}
        wide
        testId={`ap-bulk-modal-${bulkUpload.mode}`}
        footer={(
          <>
            <button
              type="button"
              className="ap-btn ap-btn-ghost"
              onClick={() => setBulkUpload({ ...bulkUpload, open: false, result: null, file: null })}
              disabled={bulkUpload.uploading}
              data-testid="ap-bulk-cancel"
            >Close</button>
            <button
              type="button"
              className="ap-btn ap-btn-accent"
              onClick={submitBulkUpload}
              disabled={!bulkUpload.file || bulkUpload.uploading}
              data-testid="ap-bulk-submit"
            >{bulkUpload.uploading ? 'Uploading…' : (bulkUpload.mode === 'older' ? 'Upload Older Rows' : 'Upload Components')}</button>
          </>
        )}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: '12px 14px', border: '1px solid var(--diis-hairline)', background: 'var(--diis-surface-sunk)', borderRadius: 'var(--diis-radius)', color: 'var(--diis-ink-muted)', fontSize: 13, lineHeight: 1.55 }}>
            {bulkUpload.mode === 'older' ? (
              <>
                <strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Required columns:</strong> Brand Name, Component Name, Size, Quantity<br />
                <strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Optional:</strong> Source Note<br />
                <strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Match rule:</strong> Component must ALREADY exist. Fresh stock is never touched.
              </>
            ) : (
              <>
                <strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Required columns:</strong> Brand Name, Component Name, Size, Stock Quantity, Low Stock Threshold<br />
                <strong style={{ color: 'var(--diis-ink)', fontWeight: 500 }}>Optional:</strong> Description, Price (defaults to ₹0)
              </>
            )}
          </div>

          <button
            type="button"
            className="ap-btn"
            onClick={downloadTemplate}
            data-testid="ap-bulk-download-template"
          >
            <Download size={15} /> Download {bulkUpload.mode === 'older' ? 'Older Stock' : 'Fresh Stock'} Template
          </button>

          <div>
            <label className="ap-field-label">Excel file (.xlsx / .xls)</label>
            <input
              ref={bulkInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setBulkUpload({ ...bulkUpload, file: e.target.files[0], result: null })}
              className="ap-input"
              data-testid="ap-bulk-file-input"
            />
          </div>

          {bulkUpload.result && (
            <div
              style={{
                padding: '12px 14px',
                border: `1px solid ${bulkUpload.result.error_count === 0 ? 'var(--diis-success)' : 'rgba(176, 74, 60, 0.22)'}`,
                background: bulkUpload.result.error_count === 0 ? 'var(--diis-success-soft)' : 'rgba(176, 74, 60, 0.06)',
                borderRadius: 'var(--diis-radius)',
                fontSize: 13, lineHeight: 1.55,
                color: bulkUpload.result.error_count === 0 ? '#14432A' : '#6B2E23',
              }}
              data-testid="ap-bulk-result"
            >
              <strong style={{ fontWeight: 600 }}>
                <FileSpreadsheet size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
                {bulkUpload.result.success_count || 0}
              </strong> {bulkUpload.mode === 'older' ? 'row(s) applied' : 'component(s) created'}
              {bulkUpload.result.error_count > 0 && (
                <>
                  <br /><strong style={{ fontWeight: 600 }}>Errors:</strong> {bulkUpload.result.error_count}
                  {bulkUpload.result.errors && (
                    <ul style={{ margin: '6px 0 0 18px' }}>
                      {bulkUpload.result.errors.slice(0, 8).map((e, i) => (
                        <li key={i} style={{ marginBottom: 2 }}>{e}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Promote modal ──────────────────────────────────── */}
      <Modal
        open={!!promoteTarget}
        onClose={() => !promoting && setPromoteTarget(null)}
        title="Promote to Re-issuable"
        subtitle="Moves verified older units into the re-issuable pool. Fresh stock is never touched."
        testId="ap-promote-modal"
        footer={(
          <>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setPromoteTarget(null)} disabled={promoting} data-testid="ap-promote-cancel">Cancel</button>
            <button type="button" className="ap-btn ap-btn-accent" onClick={submitPromote} disabled={promoting || promoteQty < 1 || (promoteTarget && promoteQty > (promoteTarget.returned_old_stock || 0))} data-testid="ap-promote-submit">
              <ArrowUpRight size={15} /> {promoting ? 'Promoting…' : `Promote ${promoteQty}`}
            </button>
          </>
        )}
      >
        {promoteTarget && (
          <>
            <dl className="ap-def-list">
              <div><dt>Component</dt><dd>{promoteTarget.name}{promoteTarget.size ? ` · ${promoteTarget.size}` : ''}</dd></div>
              <div><dt>Brand</dt><dd>{promoteTarget.brand_name || brandName(promoteTarget.brand_id)}</dd></div>
              <div><dt>Returned Old Available</dt><dd className="ap-mono-num" style={{ color: 'var(--diis-success)' }}>{promoteTarget.returned_old_stock || 0}</dd></div>
              <div><dt>Already Re-issuable</dt><dd className="ap-mono-num" style={{ color: 'var(--diis-accent-ink)' }}>{promoteTarget.reissuable_stock || 0}</dd></div>
            </dl>
            <div className="ap-mt-5">
              <label className="ap-field-label" htmlFor="ap-promote-qty">Quantity to promote</label>
              <input
                id="ap-promote-qty"
                type="number"
                min="1"
                max={Math.max(1, promoteTarget.returned_old_stock || 0)}
                className="ap-input"
                value={promoteQty}
                onChange={(e) => setPromoteQty(Math.max(1, parseInt(e.target.value || '1', 10)))}
                data-testid="ap-promote-qty"
              />
              <p className="ap-subtle ap-mt-3">
                Promoted units are drained FIRST on the next issue (used-first rotation).
              </p>
            </div>
          </>
        )}
      </Modal>

      {/* ── Add Older Stock modal (SA only) ─────────────────── */}
      <Modal
        open={addOlderOpen}
        onClose={() => !addingOlder && setAddOlderOpen(false)}
        title="Add Older Stock"
        subtitle="SuperAdmin manual entry — bypasses the clerk-verification queue. Fresh stock is never touched."
        wide
        testId="ap-add-older-modal"
        footer={(
          <>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setAddOlderOpen(false)} disabled={addingOlder} data-testid="ap-add-older-cancel">Cancel</button>
            <button type="button" className="ap-btn ap-btn-accent" onClick={submitAddOlder} disabled={!addOlderPick || addOlderQty < 1 || addingOlder} data-testid="ap-add-older-submit">
              {addingOlder ? 'Adding…' : `Add ${addOlderQty} to older stock`}
            </button>
          </>
        )}
      >
        <div className="ap-grid-2" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
          <div>
            <label className="ap-field-label">Brand</label>
            <select
              className="ap-select"
              value={addOlderSearch.brand}
              onChange={(e) => { setAddOlderSearch({ ...addOlderSearch, brand: e.target.value }); setAddOlderPick(null); }}
              data-testid="ap-add-older-brand"
            >
              <option value="">All brands</option>
              {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
            </select>
          </div>
          <div>
            <label className="ap-field-label">Component</label>
            <SearchInput
              value={addOlderSearch.component}
              onChange={(v) => { setAddOlderSearch({ ...addOlderSearch, component: v }); setAddOlderPick(null); }}
              placeholder="e.g. Healing Abutment"
              testId="ap-add-older-name"
            />
          </div>
          <div>
            <label className="ap-field-label">Size</label>
            <input
              type="text"
              className="ap-input"
              value={addOlderSearch.size}
              onChange={(e) => { setAddOlderSearch({ ...addOlderSearch, size: e.target.value }); setAddOlderPick(null); }}
              placeholder="e.g. 4.3 × 11.5"
              data-testid="ap-add-older-size"
            />
          </div>
        </div>

        <div className="ap-mt-4" style={{ border: '1px solid var(--diis-hairline)', borderRadius: 'var(--diis-radius)', maxHeight: 240, overflowY: 'auto' }} data-testid="ap-add-older-results">
          {addOlderMatches.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--diis-ink-muted)', fontSize: 13 }}>
              No components match. Adjust the search.
            </div>
          ) : addOlderMatches.map((c) => {
            const active = addOlderPick?.id === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setAddOlderPick(c)}
                data-testid={`ap-add-older-pick-${c.id}`}
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  textAlign: 'left',
                  background: active ? 'var(--diis-accent-soft)' : 'transparent',
                  border: 0,
                  borderBottom: '1px solid var(--diis-hairline)',
                  cursor: 'pointer',
                  color: 'var(--diis-ink)',
                  fontFamily: 'inherit',
                }}
              >
                <div style={{ fontWeight: 500 }}>{c.name}{c.size ? ` · ${c.size}` : ''}</div>
                <div className="ap-subtle" style={{ fontSize: 11.5 }}>
                  {c.brand_name || brandName(c.brand_id)}  ·  Older <strong>{c.returned_old_stock || 0}</strong>  ·  Fresh <strong>{c.stock_quantity || 0}</strong>
                </div>
              </button>
            );
          })}
        </div>

        <div className="ap-grid-2 ap-mt-4">
          <div>
            <label className="ap-field-label">Quantity</label>
            <input
              type="number"
              min="1"
              className="ap-input"
              value={addOlderQty}
              onChange={(e) => setAddOlderQty(Math.max(1, parseInt(e.target.value || '1', 10)))}
              data-testid="ap-add-older-qty"
            />
          </div>
          <div>
            <label className="ap-field-label">Source note (optional)</label>
            <input
              type="text"
              className="ap-input"
              value={addOlderNote}
              onChange={(e) => setAddOlderNote(e.target.value)}
              placeholder="e.g. Recovered from Q3 2024 batch"
              data-testid="ap-add-older-note"
            />
          </div>
        </div>
      </Modal>

      {/* ── Danger Zone confirmation + OTP (SuperAdmin only) ──
          Two-step: user confirms intent → OTP challenge → actual request. */}
      <Modal
        open={dangerAction === 'bulk_delete'}
        onClose={() => setDangerAction(null)}
        title="Bulk Delete Components"
        subtitle="This removes every component in the catalogue."
        testId="ap-bulk-delete-confirm"
        footer={(
          <>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setDangerAction(null)} data-testid="ap-bulk-delete-cancel">Cancel</button>
            <button
              type="button"
              className="ap-btn ap-btn-danger"
              onClick={() => { setDangerAction(null); setOtpAction('bulk_delete'); }}
              disabled={components.length === 0}
              data-testid="ap-bulk-delete-continue"
            >
              <Trash2 size={14} /> Continue — email me an OTP
            </button>
          </>
        )}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          You are about to delete <strong>{components.length}</strong> component{components.length === 1 ? '' : 's'}.
          Brands, orders, returns and the waitlist are preserved. The action cannot be undone.
        </p>
      </Modal>

      <Modal
        open={dangerAction === 'master_wipe'}
        onClose={() => setDangerAction(null)}
        title="Master Wipe — Full Inventory Reset"
        subtitle="This wipes components, brands, orders, returns, waitlist, older-stock, exchanges and activity logs."
        testId="ap-master-wipe-confirm"
        footer={(
          <>
            <button type="button" className="ap-btn ap-btn-ghost" onClick={() => setDangerAction(null)} data-testid="ap-master-wipe-cancel">Cancel</button>
            <button
              type="button"
              className="ap-btn ap-btn-danger"
              onClick={() => { setDangerAction(null); setOtpAction('master_wipe'); }}
              data-testid="ap-master-wipe-continue"
            >
              <AlertTriangle size={14} /> Continue — email me an OTP
            </button>
          </>
        )}
      >
        <p style={{ fontSize: 14, lineHeight: 1.6 }}>
          <strong style={{ color: '#B04A3C' }}>Nuclear option.</strong> The system will be returned to a first-install
          state for inventory. Users and defaulter overrides are kept. The action cannot be undone.
        </p>
      </Modal>

      <OTPModal
        open={otpAction === 'bulk_delete'}
        action="bulk_delete"
        context={`${components.length} component${components.length === 1 ? '' : 's'}`}
        confirmLabel={dangerBusy ? 'Deleting…' : 'Delete all'}
        danger
        onCancel={() => setOtpAction(null)}
        onVerified={runBulkDelete}
      />
      <OTPModal
        open={otpAction === 'master_wipe'}
        action="master_wipe"
        context="full inventory reset"
        confirmLabel={dangerBusy ? 'Wiping…' : 'Wipe inventory'}
        danger
        onCancel={() => setOtpAction(null)}
        onVerified={runMasterWipe}
      />
    </div>
  );
}
